import { Link } from 'react-router-dom';
import { Linkedin, Mail, ArrowUpRight } from 'lucide-react';
import { ZendataLogoWhite } from '../brand/ZendataLogo';

const EMAIL = 'support@zendatasolutions.net';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    { name: 'Cloud Services', path: '/services#cloud' },
    { name: 'Application Development', path: '/services#appdev' },
    { name: 'Data & Analytics', path: '/services#data' },
    { name: 'AI & Machine Learning', path: '/services#ai' },
    { name: 'Project Management', path: '/services#pm' },
    { name: 'RPA', path: '/services#rpa' },
  ];

  return (
    <footer className="bg-slate-900 text-white relative overflow-hidden" data-testid="footer">
      {/* Watermark */}
      <div className="footer-watermark text-white select-none" style={{ fontFamily: 'var(--font-heading)' }}>
        ZENDATA
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6" data-testid="footer-logo">
              <ZendataLogoWhite size="default" />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Canadian IT consulting organization with a strong focus on Analytics, AI & Digital transformation solutions.
            </p>
            <div className="flex items-center gap-4">
              <a 
                href="https://linkedin.com/company/zendatasolutions" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin size={18} />
              </a>
              <a 
                href={`mailto:${EMAIL}`}
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"
                data-testid="footer-email"
              >
                <Mail size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold tracking-widest uppercase text-slate-400 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-slate-300 hover:text-white transition-colors text-sm flex items-center gap-1 group"
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold tracking-widest uppercase text-slate-400 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Services
            </h4>
            <ul className="space-y-3">
              {services.map((service, idx) => (
                <li key={idx}>
                  <Link 
                    to={service.path}
                    className="text-slate-300 hover:text-white transition-colors text-sm flex items-center gap-1 group"
                  >
                    {service.name}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold tracking-widest uppercase text-slate-400 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Get in Touch
            </h4>
            <div className="space-y-4">
              <p className="text-slate-300 text-sm">
                <span className="block text-slate-500 text-xs uppercase tracking-wider mb-1">Email</span>
                <a href={`mailto:${EMAIL}`} className="hover:text-white transition-colors" data-testid="footer-contact-email">
                  {EMAIL}
                </a>
              </p>
              <p className="text-slate-300 text-sm">
                <span className="block text-slate-500 text-xs uppercase tracking-wider mb-1">Location</span>
                Toronto, Canada
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-16 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © {currentYear} Zendata Solutions. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/contact" className="text-slate-500 hover:text-white text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/contact" className="text-slate-500 hover:text-white text-sm transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
